import { useCallback, useEffect, useRef, useState } from 'react'
import { DeckMode } from 'common/deckMode'
import { debounce } from 'lodash'
import { useKeyPress } from './useKeyboardShortcut'
import { useMaintenance } from './maintenance/useMaintenance'

export const useMultiSelect = ({
  onPanelClick,
  onSelectChange,
  awaitingActionId,
  type,
  selected: selectedPanel,
  mode = 'default'
}) => {
  const canMultiSelect = mode !== 'default'
  const [isControlActivated, setIsControlActivated] = useState(false)
  const { actions } = useMaintenance()

  const panelAndGroup = useRef({})

  useKeyPress([!/Mac/.test(navigator.platform) ? 'Control' : 'Meta'], (event) => {
    setIsControlActivated(event.type === 'keydown')
  })

  const reset = useCallback(() => {
    onPanelClick([])
    panelAndGroup.current = {}
  }, [])

  useEffect(() => {
    if (mode == 'default') {
      reset()
    }
  }, [mode, reset])

  const getUniquePanels = useCallback((data, removeDuplicates = false) => {
    const objects = {}
    for (const item of data) {
      if (item === 'cancel' || typeof item === 'string') {
        continue
      }
      const key =
        typeof item.StartPosition === 'undefined'
          ? `${item.Position.Row}-${item.Position.Column}`
          : `${item.StartPosition}-${item.Side}`

      if (!(key in objects)) {
        objects[key] = item
      } else if (removeDuplicates) {
        delete objects[key]
      }
    }

    return Object.values(objects)
  }, [])

  const onPanelClick_ = useCallback(
    (data, e) => {
      e?.stopPropagation?.()
      if (awaitingActionId === actions.Swap.id || awaitingActionId === actions.Relocate.id) {
        onPanelClick(Array.isArray(data) ? data[0] : data, e)
        return
      }
      const selected = getUniquePanels((selectedPanel ?? []).concat(data))
      if (onSelectChange && (type === 'report' || mode !== DeckMode.maintenance)) {
        onSelectChange(selected, isControlActivated)
      } else {
        onPanelClick(selected)
      }
    },
    [onPanelClick, getUniquePanels, mode, type, selectedPanel, awaitingActionId, isControlActivated]
  )

  const onSinglePanelClick = useCallback(
    (data, e) => {
      data = Array.isArray(data) ? data : [data]
      if (isControlActivated) {
        onPanelClick(getUniquePanels((selectedPanel ?? []).concat(data), true), e)
      } else {
        onPanelClick(data, e)
      }
    },
    [onPanelClick, getUniquePanels, awaitingActionId, isControlActivated, selectedPanel]
  )

  return {
    onPanelClick: onPanelClick_,
    onSinglePanelClick: debounce(onSinglePanelClick, 300),
    canMultiSelect,
    reset
  }
}
